import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, BookOpen, ChatCircleDots, Link as LinkIcon, Clipboard, Check, FilePdf, Download, Robot, User, Wrench, Plus, Trash, SpinnerGap, MagicWand, PencilSimple, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const downloadPdf = async (url, filename) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename || 'Welcome-Guide.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  } catch { alert('Failed to download PDF'); }
};

const KB_FIELDS = [
  { key: 'wifi_name', label: 'WiFi Network Name', placeholder: 'MyHomeWiFi' },
  { key: 'wifi_password', label: 'WiFi Password', placeholder: 'password123' },
  { key: 'check_in_time', label: 'Check-in Time', placeholder: '3:00 PM' },
  { key: 'check_out_time', label: 'Check-out Time', placeholder: '11:00 AM' },
  { key: 'check_in_instructions', label: 'Check-in Instructions', placeholder: 'Use the lockbox...', type: 'textarea' },
  { key: 'check_out_instructions', label: 'Check-out Instructions', placeholder: 'Leave keys on counter...', type: 'textarea' },
  { key: 'house_rules', label: 'House Rules', placeholder: 'No smoking...', type: 'textarea' },
  { key: 'parking_info', label: 'Parking Info', placeholder: 'Free street parking...', type: 'textarea' },
  { key: 'emergency_contacts', label: 'Emergency Contacts', placeholder: 'Police: 911...', type: 'textarea' },
  { key: 'local_recommendations', label: 'Your Personal Recommendations (Optional)', placeholder: 'Your favorite local spots...', type: 'textarea' },
  { key: 'custom_info', label: 'Additional Info', placeholder: 'Appliance instructions, etc.', type: 'textarea' },
];

const SERVICE_TYPES = ['cleaning', 'maintenance', 'plumbing', 'electrical', 'landscaping', 'pool', 'pest control', 'hvac', 'locksmith', 'other'];

export default function PropertyDetail() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [kb, setKb] = useState({});
  const [sessions, setSessions] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [tab, setTab] = useState('knowledge');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedChat, setExpandedChat] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showContractorForm, setShowContractorForm] = useState(false);
  const [contractorForm, setContractorForm] = useState({ property_id: '', name: '', service_type: 'cleaning', phone: '', email: '', rate: '', notes: '' });
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get(`/properties/${id}`),
      api.get(`/properties/${id}/knowledge-base`),
      api.get(`/chat-sessions/${id}`),
      api.get(`/properties/${id}/contractors`),
    ]).then(([propRes, kbRes, sessRes, conRes]) => {
      setProperty(propRes.data);
      setKb(kbRes.data);
      setSessions(sessRes.data);
      setContractors(conRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id, api]);

  const saveKb = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${id}/knowledge-base`, kb);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { alert('Error saving'); } finally { setSaving(false); }
  };

  const generateLocalGuide = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/properties/${id}/generate-local-guide`);
      setKb(prev => ({ ...prev, local_guide: data.local_guide }));
    } catch (err) {
      alert('Failed to generate local guide. Please try again.');
    } finally { setGenerating(false); }
  };

  const addContractor = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${id}/contractors`, { ...contractorForm, property_id: id });
      const { data } = await api.get(`/properties/${id}/contractors`);
      setContractors(data);
      setShowContractorForm(false);
      setContractorForm({ property_id: '', name: '', service_type: 'cleaning', phone: '', email: '', rate: '', notes: '' });
    } catch { alert('Error adding contractor'); }
  };

  const deleteContractor = async (cid) => {
    if (!window.confirm('Remove this contractor?')) return;
    await api.delete(`/properties/${id}/contractors/${cid}`);
    setContractors(prev => prev.filter(c => c.id !== cid));
  };

  const openEdit = () => {
    setEditForm({
      name: property.name || '',
      address: property.address || '',
      property_type: property.property_type || 'apartment',
      bedrooms: property.bedrooms || 1,
      bathrooms: property.bathrooms || 1,
      max_guests: property.max_guests || 2,
      description: property.description || '',
      nightly_rate: property.nightly_rate || 100,
      image_url: property.image_url || '',
    });
    setEditError('');
    setShowEdit(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name?.trim() || !editForm.address?.trim()) {
      setEditError('Name and address are required.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    const addressChanged = editForm.address.trim() !== (property.address || '').trim();
    try {
      const { data } = await api.put(`/properties/${id}`, editForm);
      setProperty(data);
      setShowEdit(false);
      // If the address changed AND a local guide already exists, offer to regenerate it.
      if (addressChanged && hasLocalGuide) {
        if (window.confirm('Address updated. Re-generate the AI local guide (restaurants, hospitals, attractions) to match the new address?')) {
          await generateLocalGuide();
        }
      }
    } catch (err) {
      setEditError(err?.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteProperty = async () => {
    if (!window.confirm(`Delete "${property.name}"? This removes the property, its knowledge base, and all bookings. This cannot be undone.`)) return;
    try {
      await api.delete(`/properties/${id}`);
      navigate('/properties');
    } catch {
      alert('Failed to delete property.');
    }
  };

  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const chatUrl = `${window.location.origin}/chat/${id}`;
  const pdfUrl = `${API}/api/properties/${id}/welcome-guide`;
  const copyLink = () => { navigator.clipboard.writeText(chatUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const totalMessages = sessions.reduce((sum, s) => sum + (s.messages?.length || 0), 0);
  const aiResponses = sessions.reduce((sum, s) => sum + (s.messages?.filter(m => m.role === 'assistant')?.length || 0), 0);
  const localGuide = kb?.local_guide || {};
  const hasLocalGuide = Object.keys(localGuide).length > 0;

  if (loading) return <p className="text-sm text-zinc-500">Loading...</p>;
  if (!property) return <p className="text-sm text-red-500">Property not found.</p>;

  return (
    <div data-testid="property-detail">
      <button onClick={() => navigate('/properties')} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="back-to-properties">
        <ArrowLeft size={14} /> Back to Properties
      </button>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{property.name}</h1>
          <p className="text-sm text-zinc-500">{property.address}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={openEdit} className="px-3 py-2 bg-white border border-zinc-300 text-zinc-900 text-xs font-bold rounded-sm hover:bg-zinc-50 flex items-center gap-1.5" data-testid="edit-property-btn">
            <PencilSimple size={14} weight="bold" /> Edit details
          </button>
          {hasLocalGuide && (
            <button onClick={() => { if (window.confirm('Re-generate the AI local guide using the current address? This replaces the existing nearby restaurants, hospitals, attractions, etc.')) generateLocalGuide(); }} disabled={generating} className="px-3 py-2 bg-white border border-zinc-300 text-zinc-900 text-xs font-bold rounded-sm hover:bg-zinc-50 flex items-center gap-1.5 disabled:opacity-60" data-testid="regenerate-local-guide-btn" title="Re-run AI to rebuild local recommendations from the current address">
              {generating ? <><SpinnerGap size={14} weight="bold" className="animate-spin" /> Regenerating…</> : <><MagicWand size={14} weight="bold" /> Refresh local guide</>}
            </button>
          )}
          <button onClick={() => downloadPdf(pdfUrl, `${property?.name || 'Welcome'}-Guide.pdf`)} className="px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-sm hover:bg-red-700 flex items-center gap-1.5" data-testid="download-pdf-btn">
            <FilePdf size={14} weight="bold" /> Welcome Guide PDF
          </button>
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 text-xs text-zinc-600 rounded-sm max-w-[200px] truncate" data-testid="chat-link-display">
            <LinkIcon size={14} /> <span className="truncate">{chatUrl}</span>
          </div>
          <button onClick={copyLink} className="px-3 py-2 bg-zinc-900 text-white text-xs font-bold rounded-sm hover:bg-zinc-800 flex items-center gap-1.5" data-testid="copy-chat-link">
            {copied ? <><Check size={12} /> Copied</> : <><Clipboard size={12} /> Copy</>}
          </button>
        </div>
      </div>

      {/* Edit Property Modal */}
      {showEdit && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="edit-property-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => !editSaving && setShowEdit(false)} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>Edit property details</h2>
                <p className="text-xs text-zinc-500 mt-0.5">Fix a typo in the address, bump the nightly rate, refresh the photo — updates apply immediately.</p>
              </div>
              <button onClick={() => !editSaving && setShowEdit(false)} className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm" data-testid="edit-property-close">
                <X size={16} weight="bold" />
              </button>
            </div>
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              {editError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="edit-error">{editError}</div>}
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Name *</label>
                <input value={editForm.name} onChange={e => setEdit('name', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="edit-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Address *</label>
                <input value={editForm.address} onChange={e => setEdit('address', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="edit-address-input" />
                <p className="text-[11px] text-zinc-400 mt-1">Full street address, city, region/state, country — used by the AI concierge for local recommendations.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Property type</label>
                  <select value={editForm.property_type} onChange={e => setEdit('property_type', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="edit-type-input">
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="villa">Villa</option>
                    <option value="condo">Condo</option>
                    <option value="cabin">Cabin</option>
                    <option value="loft">Loft</option>
                    <option value="studio">Studio</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Nightly rate ($)</label>
                  <input type="number" min="0" value={editForm.nightly_rate} onChange={e => setEdit('nightly_rate', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="edit-rate-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Bedrooms</label>
                  <input type="number" min="0" value={editForm.bedrooms} onChange={e => setEdit('bedrooms', parseInt(e.target.value) || 0)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="edit-bedrooms-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Bathrooms</label>
                  <input type="number" min="0" step="0.5" value={editForm.bathrooms} onChange={e => setEdit('bathrooms', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="edit-bathrooms-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Max guests</label>
                  <input type="number" min="1" value={editForm.max_guests} onChange={e => setEdit('max_guests', parseInt(e.target.value) || 1)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" data-testid="edit-guests-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Description</label>
                <textarea value={editForm.description} onChange={e => setEdit('description', e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm resize-none" data-testid="edit-description-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Property image URL <span className="text-zinc-400 font-normal">(optional)</span></label>
                <input value={editForm.image_url} onChange={e => setEdit('image_url', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" placeholder="https://..." data-testid="edit-image-input" />
              </div>
              <div className="flex gap-3 pt-3 border-t border-zinc-100">
                <button type="button" onClick={deleteProperty} className="px-3 py-2.5 border border-red-200 text-red-700 text-sm font-bold rounded-sm hover:bg-red-50 inline-flex items-center gap-1.5" data-testid="edit-delete-btn">
                  <Trash size={14} weight="bold" /> Delete
                </button>
                <div className="flex-1" />
                <button type="button" onClick={() => setShowEdit(false)} disabled={editSaving} className="px-4 py-2.5 border border-zinc-200 text-sm font-bold text-zinc-700 rounded-sm hover:bg-zinc-50 disabled:opacity-50" data-testid="edit-cancel-btn">Cancel</button>
                <button type="submit" disabled={editSaving} className="px-5 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="edit-save-btn">
                  {editSaving ? <><SpinnerGap size={14} className="animate-spin" /> Saving…</> : <>Save changes</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-0 border border-zinc-200 bg-white mb-6" data-testid="property-stats">
        <div className="p-4 border-r border-zinc-200 text-center">
          <p className="text-xl font-black text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>{sessions.length}</p>
          <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Conversations</p>
        </div>
        <div className="p-4 border-r border-zinc-200 text-center">
          <p className="text-xl font-black text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>{totalMessages}</p>
          <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Total Messages</p>
        </div>
        <div className="p-4 text-center">
          <p className="text-xl font-black text-[#0052FF]" style={{ fontFamily: 'var(--font-heading)' }}>{aiResponses}</p>
          <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">AI Responses</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-zinc-200 mb-6 overflow-x-auto">
        {[
          { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
          { id: 'local', label: `Local Guide ${hasLocalGuide ? '' : ''}`, icon: MagicWand },
          { id: 'contractors', label: `Contractors (${contractors.length})`, icon: Wrench },
          { id: 'chats', label: `Chats (${sessions.length})`, icon: ChatCircleDots },
          { id: 'guide', label: 'Welcome PDF', icon: FilePdf },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`} data-testid={`tab-${t.id}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Knowledge Base Tab */}
      {tab === 'knowledge' && (
        <div className="bg-white border border-zinc-200" data-testid="knowledge-base-editor">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Property Knowledge Base</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Powers the AI concierge and Welcome Guide PDF</p>
            </div>
            <button onClick={saveKb} disabled={saving} className="px-4 py-2 bg-[#0052FF] text-white text-xs font-bold rounded-sm hover:bg-[#003EDD] disabled:opacity-50 flex items-center gap-1.5" data-testid="save-kb-btn">
              {saved ? <><Check size={12} /> Saved</> : saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {KB_FIELDS.map(f => (
              <div key={f.key} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea value={kb[f.key] || ''} onChange={e => setKb(prev => ({ ...prev, [f.key]: e.target.value }))} rows={3} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm resize-none" placeholder={f.placeholder} data-testid={`kb-${f.key}`} />
                ) : (
                  <input value={kb[f.key] || ''} onChange={e => setKb(prev => ({ ...prev, [f.key]: e.target.value }))} className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm" placeholder={f.placeholder} data-testid={`kb-${f.key}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Local Guide Tab */}
      {tab === 'local' && (
        <div className="bg-white border border-zinc-200" data-testid="local-guide-tab">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>AI-Generated Local Guide</h2>
              <p className="text-xs text-zinc-500 mt-0.5">AI researches restaurants, hospitals, attractions, shopping & more based on your property address</p>
            </div>
            <button onClick={generateLocalGuide} disabled={generating} className="px-4 py-2 bg-[#0052FF] text-white text-xs font-bold rounded-sm hover:bg-[#003EDD] disabled:opacity-50 flex items-center gap-1.5" data-testid="generate-guide-btn">
              {generating ? <><SpinnerGap size={14} className="animate-spin" /> Generating...</> : <><MagicWand size={14} /> {hasLocalGuide ? 'Regenerate' : 'Generate Local Guide'}</>}
            </button>
          </div>

          {!hasLocalGuide ? (
            <div className="p-12 text-center">
              <MagicWand size={40} weight="duotone" className="text-[#0052FF] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Let AI do the research</h3>
              <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">Click "Generate Local Guide" and AI will automatically research nearby restaurants, hospitals, attractions, shopping, and more based on your property's address.</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {[
                { key: 'restaurants', title: 'Restaurants', fields: ['cuisine', 'price_range', 'distance', 'description', 'must_try'] },
                { key: 'cafes_and_bars', title: 'Cafes & Bars', fields: ['type', 'distance', 'known_for'] },
                { key: 'medical_and_emergency', title: 'Medical & Emergency Services', fields: ['type', 'address', 'phone', 'distance', 'hours'] },
                { key: 'sightseeing_and_attractions', title: 'Sightseeing & Attractions', fields: ['type', 'distance', 'description', 'tip'] },
                { key: 'outdoor_activities', title: 'Outdoor Activities', fields: ['type', 'distance', 'description'] },
                { key: 'groceries_and_shopping', title: 'Shopping & Groceries', fields: ['type', 'distance', 'hours', 'notes'] },
                { key: 'transportation', title: 'Transportation', fields: ['type', 'details'] },
                { key: 'family_friendly', title: 'Family Friendly', fields: ['type', 'description', 'ages'] },
                { key: 'nightlife', title: 'Nightlife', fields: ['type', 'distance', 'description'] },
              ].map(section => {
                const items = localGuide[section.key];
                if (!items || items.length === 0) return null;
                return (
                  <div key={section.key} data-testid={`guide-section-${section.key}`}>
                    <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wide mb-3 pb-2 border-b border-zinc-200" style={{ fontFamily: 'var(--font-heading)' }}>{section.title}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {items.map((item, i) => (
                        <div key={i} className="p-3 border border-zinc-100 hover:border-zinc-200 transition-colors rounded-sm">
                          <p className="text-sm font-bold text-zinc-900">{item.name}</p>
                          <div className="mt-1 space-y-0.5">
                            {section.fields.map(f => item[f] ? (
                              <p key={f} className="text-xs text-zinc-500"><span className="font-medium text-zinc-700 capitalize">{f.replace(/_/g, ' ')}:</span> {item[f]}</p>
                            ) : null)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {localGuide.practical_tips?.length > 0 && (
                <div data-testid="guide-section-tips">
                  <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wide mb-3 pb-2 border-b border-zinc-200" style={{ fontFamily: 'var(--font-heading)' }}>Local Tips</h3>
                  <ul className="space-y-2">
                    {localGuide.practical_tips.map((tip, i) => (
                      <li key={i} className="text-sm text-zinc-700 flex items-start gap-2">
                        <span className="text-[#0052FF] font-bold mt-0.5">-</span> {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Contractors Tab */}
      {tab === 'contractors' && (
        <div className="bg-white border border-zinc-200" data-testid="contractors-tab">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Contractors & Vendors</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Manage your cleaning, maintenance, and service providers for this property</p>
            </div>
            <button onClick={() => setShowContractorForm(true)} className="px-4 py-2 bg-[#0052FF] text-white text-xs font-bold rounded-sm hover:bg-[#003EDD] flex items-center gap-1.5" data-testid="add-contractor-btn">
              <Plus size={14} /> Add Contractor
            </button>
          </div>

          {showContractorForm && (
            <div className="px-6 py-4 border-b border-zinc-100 bg-zinc-50">
              <form onSubmit={addContractor} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input value={contractorForm.name} onChange={e => setContractorForm(f => ({ ...f, name: e.target.value }))} required className="px-3 py-2 border border-zinc-200 text-sm rounded-sm" placeholder="Contractor name" data-testid="contractor-name" />
                <select value={contractorForm.service_type} onChange={e => setContractorForm(f => ({ ...f, service_type: e.target.value }))} className="px-3 py-2 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="contractor-type">
                  {SERVICE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <input value={contractorForm.phone} onChange={e => setContractorForm(f => ({ ...f, phone: e.target.value }))} className="px-3 py-2 border border-zinc-200 text-sm rounded-sm" placeholder="Phone" data-testid="contractor-phone" />
                <input value={contractorForm.email} onChange={e => setContractorForm(f => ({ ...f, email: e.target.value }))} className="px-3 py-2 border border-zinc-200 text-sm rounded-sm" placeholder="Email" data-testid="contractor-email" />
                <input value={contractorForm.rate} onChange={e => setContractorForm(f => ({ ...f, rate: e.target.value }))} className="px-3 py-2 border border-zinc-200 text-sm rounded-sm" placeholder="Rate (e.g. $150/visit)" data-testid="contractor-rate" />
                <input value={contractorForm.notes} onChange={e => setContractorForm(f => ({ ...f, notes: e.target.value }))} className="px-3 py-2 border border-zinc-200 text-sm rounded-sm" placeholder="Notes" data-testid="contractor-notes" />
                <div className="md:col-span-3 flex gap-2">
                  <button type="submit" className="px-4 py-2 bg-[#0052FF] text-white text-xs font-bold rounded-sm" data-testid="save-contractor-btn">Save</button>
                  <button type="button" onClick={() => setShowContractorForm(false)} className="px-4 py-2 border border-zinc-200 text-xs font-bold text-zinc-600 rounded-sm">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {contractors.length === 0 ? (
            <div className="p-12 text-center">
              <Wrench size={32} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">No contractors yet. Add your cleaning, maintenance, and service providers.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {contractors.map((c, i) => (
                <div key={c.id} className="px-6 py-4 flex items-center justify-between hover:bg-zinc-50" data-testid={`contractor-${i}`}>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-bold text-zinc-900">{c.name}</p>
                      <span className="text-xs px-2 py-0.5 font-bold bg-zinc-100 text-zinc-600 rounded-sm">{c.service_type}</span>
                    </div>
                    <div className="flex gap-4 text-xs text-zinc-500">
                      {c.phone && <span>{c.phone}</span>}
                      {c.email && <span>{c.email}</span>}
                      {c.rate && <span className="font-bold text-zinc-700">{c.rate}</span>}
                      {c.notes && <span>{c.notes}</span>}
                    </div>
                  </div>
                  <button onClick={() => deleteContractor(c.id)} className="text-zinc-400 hover:text-red-600" data-testid={`delete-contractor-${i}`}>
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Guest Chats Tab */}
      {tab === 'chats' && (
        <div className="bg-white border border-zinc-200" data-testid="chat-sessions-list">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Guest Chat History</h2>
          </div>
          {sessions.length === 0 ? (
            <div className="p-12 text-center">
              <ChatCircleDots size={32} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">No guest chats yet. Share the chat link with your guests.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {sessions.map((s, i) => (
                <div key={i} data-testid={`chat-session-${i}`}>
                  <button onClick={() => setExpandedChat(expandedChat === i ? null : i)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-zinc-50 text-left" data-testid={`chat-session-toggle-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center"><Robot size={16} weight="duotone" className="text-violet-600" /></div>
                      <div>
                        <p className="text-sm font-bold text-zinc-900">{s.guest_name || 'Guest'}</p>
                        <p className="text-xs text-zinc-500">{s.messages?.length || 0} messages</p>
                      </div>
                    </div>
                    <span className="text-xs text-zinc-400">{expandedChat === i ? 'Collapse' : 'View'}</span>
                  </button>
                  {expandedChat === i && (
                    <div className="px-6 pb-4 bg-zinc-50 border-t border-zinc-100">
                      <div className="pt-3 space-y-2 max-h-96 overflow-y-auto">
                        {s.messages?.map((m, j) => (
                          <div key={j} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`flex items-start gap-2 max-w-[80%] ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? 'bg-zinc-300' : 'bg-[#0052FF]'}`}>
                                {m.role === 'user' ? <User size={12} className="text-zinc-600" /> : <Robot size={12} className="text-white" />}
                              </div>
                              <div className={`px-3 py-2 text-sm rounded-sm ${m.role === 'user' ? 'bg-zinc-200 text-zinc-800' : 'bg-white border border-zinc-200 text-zinc-800'}`}>{m.content}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Welcome Guide Tab */}
      {tab === 'guide' && (
        <div className="bg-white border border-zinc-200" data-testid="welcome-guide-tab">
          <div className="p-8 text-center">
            <div className="w-16 h-20 bg-red-50 border border-red-200 rounded-sm mx-auto mb-4 flex items-center justify-center"><FilePdf size={32} weight="duotone" className="text-red-500" /></div>
            <h3 className="text-lg font-bold text-zinc-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{property.name} — Welcome Guide</h3>
            <p className="text-sm text-zinc-500 mb-2 max-w-md mx-auto">Complete guest guide with property photo, WiFi, check-in/out, house rules, and AI-researched local info.</p>
            {!hasLocalGuide && <p className="text-xs text-amber-600 font-bold mb-4">Generate the Local Guide first for a richer PDF!</p>}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => downloadPdf(pdfUrl, `${property?.name || 'Welcome'}-Guide.pdf`)} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white text-sm font-bold rounded-sm hover:bg-red-700" data-testid="download-guide-btn"><Download size={16} weight="bold" /> Download PDF</button>
              <button onClick={() => { navigator.clipboard.writeText(pdfUrl); }} className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-200 text-zinc-700 text-sm font-bold rounded-sm hover:bg-zinc-50" data-testid="copy-guide-link-btn"><LinkIcon size={16} /> Copy PDF Link</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
